import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import { ShareIcon } from '@heroicons/react/24/outline';
import cardDetails from '../../components/assets/information/CardDetails';
import { WandsIcon, CupsIcon, SwordsIcon, PentaclesIcon, MajorArcanaIcon, MapIcon } from '../../components/assets/images/SuitIcons';
import DarkModeToggle from '../../components/DarkModeToggle';
import { Dialog } from '../../components/dialog';

const courtCardImages = {
  King: '/king.svg',
  Queen: '/queen.svg',
  Knight: '/knight.svg',
  Page: '/page.svg',
};

const numberMapping = {
  Ace: 'A',
  Two: '2',
  Three: '3',
  Four: '4',
  Five: '5',
  Six: '6',
  Seven: '7',
  Eight: '8',
  Nine: '9',
  Ten: '10',
};

const TarotMap = () => {
  const { uniqueId } = useParams(); // Extract the UUID from the URL
  const location = useLocation();
  const [sessionData, setSessionData] = useState(null);
  const [error, setError] = useState(null);
  const [openLayoutModal, setOpenLayoutModal] = useState(null); // index of open modal, or null

  useEffect(() => {
    const fetchSessionData = async () => {
      try {
        console.log('Looking for session with uniqueId:', uniqueId);
        
        // Check if session data was passed directly (for database permission workaround)
        if (location.state?.use_passed_data && location.state?.session_data) {
          console.log('Using passed session data instead of database fetch');
          console.log('Passed session data:', location.state.session_data);
          setSessionData(location.state.session_data);
          return;
        }
        
        // Also check if session_data exists without the flag (for successful database saves)
        if (location.state?.session_data) {
          console.log('Found session_data in location state (without flag), using it');
          console.log('Session data:', location.state.session_data);
          setSessionData(location.state.session_data);
          return;
        }
        
        // First try to find by reading_map_url
        let { data, error } = await supabase
          .from('guest_sessions')
          .select('*')
          .eq('reading_map_url', uniqueId) // Match against UUID
          .limit(1);

        // If not found by reading_map_url, try searching by guest_uid as fallback
        if ((!data || data.length === 0) && !error) {
          console.log('No session found by reading_map_url, trying guest_uid...');
          
          // First, let's see what sessions actually exist
          const { data: allSessions } = await supabase
            .from('guest_sessions')
            .select('guest_uid, reading_question, reading_type')
            .limit(10);
          console.log('All existing sessions:', allSessions);
          
          const fallbackResult = await supabase
            .from('guest_sessions')
            .select('*')
            .eq('guest_uid', uniqueId) // Try using uniqueId as guest_uid
            .limit(1);
          
          data = fallbackResult.data;
          error = fallbackResult.error;
          console.log('Fallback search result:', { data, error });
          
          // If still no data, try getting the most recent session as emergency fallback
          if (!data || data.length === 0) {
            console.log('No session found by guest_uid either, trying most recent session...');
            const recentResult = await supabase
              .from('guest_sessions')
              .select('*')
              .order('created_at', { ascending: false })
              .limit(1);
            
            if (recentResult.data && recentResult.data.length > 0) {
              console.log('Using most recent session as fallback:', recentResult.data[0]);
              data = recentResult.data;
              error = recentResult.error;
            }
          }
        }

        if (error) {
          console.error('Error fetching session data:', error.message);
          setError(error.message);
        } else if (!data || data.length === 0) {
          console.error('No session found for ID:', uniqueId);
          setError('Reading session not found. The link may be invalid or expired.');
        } else {
          console.log('Found session data:', data[0]);
          setSessionData(data[0]); // Take the first result
        }
      } catch (err) {
        console.error('Unexpected error:', err);
        setError('An unexpected error occurred.');
      }
    };

    fetchSessionData();
  }, [uniqueId, location.state]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Your TarotMap',
          text: `Check out my TarotMap reading: ${sessionData?.reading_type}`,
          url: window.location.href,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      alert('Sharing is not supported on this device.');
    }
  };

  if (error) return <div className="bg-white dark:bg-[\#1a1a1a] text-gray-900 dark:text-[#E7E7E7] min-h-screen flex items-center justify-center">Error: {error}</div>;
  if (!sessionData) return <div className="bg-white dark:bg-[\#1a1a1a] text-gray-900 dark:text-[#E7E7E7] min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="bg-white dark:bg-[#1a1a1a] py-10 min-h-screen">
      
      <header>
        <div className="mx-2 place-self-end px-5 sm:px-5 lg:px-5 flex items-center justify-end gap-4">
          {/* Dark mode toggle positioned in top left */}
          <DarkModeToggle />
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"
            onClick={handleShare}
          >
            <span className="sr-only">Share</span>
            <ShareIcon aria-hidden="true" className="size-6" />
          </button>
        </div>
      </header>
      <div className="mx-auto max-w-2xl px-6 lg:max-w-7xl lg:px-8">
        
      <a href="/"><img alt="TarotMap" src="/favicon.ico" className="mx-auto h-10 w-auto" /></a>

        <h2 className="text-center text-base/7 font-semibold text-neutral-500 dark:text-[#D4D4D4]">
          Your TarotMap
        </h2>
        <p className="mx-auto mt-6 mb-6 text-center text-4xl font-semibold tracking-tight text-balance text-gray-950 dark:text-[#D4D4D4] sm:text-4xl">
          {sessionData.reading_type}
        </p>
        <p className="mx-auto mt-4 text-center text-sm font-semibold tracking-tight text-balance text-stone-500 dark:text-stone-300">
          Reading focus:</p>
        <p className="mx-auto mt-2 text-center text-sm font-semibold tracking-tight text-balance text-gray-800 dark:text-[#FFFFFF]">
         {sessionData.reading_question}
        </p>

        <div className={`mt-10 grid gap-4 lg:grid-rows-auto lg:items-start ${
          sessionData.step_data.length === 1 
            ? 'lg:grid-cols-1 justify-center max-w-md mx-auto' 
            : 'lg:grid-cols-3'
        }`}>
          {sessionData.step_data.map((step, index) => {
            const suitIcon =
              cardDetails[step.selectedSuit]?.icon || null;

            return (
              <div
                key={index}
                className="relative flex lg:row-span-2 flex-col row-auto overflow-hidden rounded-lg bg-white dark:bg-[#252525] shadow-sm ring-1 ring-black/5 dark:ring-gray-700/50"
              >
                {/* Step Header - Compact 2-column layout */}
                <div className="px-10 mt-6 sm:px-10 sm:pt-6 flex items-start gap-4 min-h-[80px]">
                  {/* Column 1: Step heading + map icon stacked */}
                  <div className="flex flex-col items-start flex-shrink-0">
                    <p className="text-md font-medium text-gray-950 dark:text-gray-100">
                      Step {step.step}:
                    </p>
                    {step.stepMap && (
                      <button
                        className="mt-2 flex items-center gap-1 rounded-full bg-neutral-200/60 dark:bg-neutral-700/60 px-2 py-1 hover:bg-neutral-100/80 dark:hover:bg-neutral-700/30 transition"
                        title="Show card layout"
                        onClick={() => setOpenLayoutModal(index)}
                      >
                        <MapIcon className="w-5 h-5 text-neutral-600 dark:text-[#D4D4D4]" />
                        <span className="sr-only">Show card layout</span>
                      </button>
                    )}
                  </div>
                  
                  {/* Column 2: Step meaning (takes remaining space) */}
                  <p className="text-sm/6 text-stone-500 dark:text-stone-300 flex-1">
                    {step.stepMeaning}
                  </p>
                </div>
                
       {/* card details */}
         <div className="px-10 mt-6 sm:px-10 sm:pt-10 pb-8">
           <div className="rounded-lg px-10 bg-white dark:bg-[#2F2F2F] p-4 shadow-lg border border-neutral-200/20 dark:border-neutral-700/40 relative overflow-hidden">

        {/* Card Info */}
        <div className="flex flex-col items-center mb-6">
          {/* Suit Icons and Card Face in a flex row */}
          <div className="w-full flex flex-row justify-between items-start mt-8 mb-4 relative">
            {/* Left Suit Icon */}
            {(() => {
              const iconComponent = {
                wands: WandsIcon,
                rods: WandsIcon,
                cups: CupsIcon,
                swords: SwordsIcon,
                pentacles: PentaclesIcon,
                majorArcana: MajorArcanaIcon,
              }[step.selectedSuit] || WandsIcon;
              return iconComponent ? React.createElement(iconComponent, { className: 'w-10 h-10 text-neutral-600 dark:text-[#D4D4D4]', strokeWidth: 1 }) : null;
            })()}
            {/* Card Face (image or text), flipped if reversed */}
            <div className="flex flex-col items-center flex-1">
              <div className="mt-20 mb-2 min-h-28">
                <span className={["transition-transform duration-300 inline-block", (step.isReversed === true || step.isReversed === 'true') ? 'rotate-180' : ''].join(' ')}>
                  {['King', 'Queen', 'Knight', 'Page'].includes(step.cardDetailsForSelectedCard.name) ? (
                    <img src={courtCardImages[step.cardDetailsForSelectedCard.name]} alt={`${step.cardDetailsForSelectedCard.name} icon`} className="w-28 h-28" />
                  ) : (
                    <span className="text-4xl text-neutral-600 dark:text-[#D4D4D4] font-bold text-center">
                      {numberMapping[step.cardDetailsForSelectedCard.name] || step.cardDetailsForSelectedCard.name}
                    </span>
                  )}
                </span>
              </div>
              <p className="text-lg font-bold text-neutral-600 dark:text-[#D4D4D4] text-center mt-2">
                {step.cardDetailsForSelectedCard.name} of {step.cardDetailsForSelectedCard.suit}
              </p>
            </div>
            {/* Right Suit Icon */}
            {(() => {
              const iconComponent = {
                wands: WandsIcon,
                rods: WandsIcon,
                cups: CupsIcon,
                swords: SwordsIcon,
                pentacles: PentaclesIcon,
                majorArcana: MajorArcanaIcon,
              }[step.selectedSuit] || WandsIcon;
              return iconComponent ? React.createElement(iconComponent, { className: 'w-10 h-10 text-neutral-600 dark:text-[#D4D4D4]', strokeWidth: 1 }) : null;
            })()}
          </div>
        </div>
        {/* Card Meanings: upright/reverse color swap if reversed, always show 'Reverse: ...' */}
        {(step.isReversed === true || step.isReversed === 'true') ? (
          <>
               <p className="mt-2 mb-6 text-sm text-stone-300 dark:text-stone-500 text-center">
              {step.cardDetailsForSelectedCard.meaning}
            </p>
             <p className="mt-2 mb-8 text-sm text-neutral-900 dark:text-white text-center">
              Reverse: {step.cardDetailsForSelectedCard.reverse}
            </p>
          </>
        ) : (
          <>
             <p className="mt-2 mb-2 text-sm text-neutral-900 dark:text-white text-center">
              {step.cardDetailsForSelectedCard.meaning}
            </p>
             <p className="mt-2 mb-6 text-sm text-stone-300 dark:text-stone-500 text-center">
              Reverse: {step.cardDetailsForSelectedCard.reverse}
            </p>
          </>
        )}
      </div>
      </div>
      {step.notes && step.notes.trim() && (
        <div className="px-8 mb-12 sm:px-10">
          <p className="mt-3 mb-2 text-sm font-bold text-gray-950 dark:text-gray-100">
            Notes:
          </p>
          <p className="px-4 text-sm text-stone-500 dark:text-stone-300 italic">{step.notes}</p>
        </div>
      )}
              </div>
            );
          })}

        </div>
      </div>
      {/* Card Layout Modal */}
      {openLayoutModal !== null && (
        <Dialog open={true} onClose={() => setOpenLayoutModal(null)} size="md">
          <div className="relative">
            <button
              onClick={() => setOpenLayoutModal(null)}
              className="absolute top-2 right-2 z-10 rounded-full p-1 text-neutral-400 hover:text-neutral-200 dark:hover:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-neutral-500"
              aria-label="Close modal"
            >
              <span style={{fontSize: '1.5rem', fontWeight: 'bold'}}>&times;</span>
            </button>
            <img
              src={`/${sessionData.step_data[openLayoutModal].stepMap}`}
              alt={`Card layout for step ${openLayoutModal + 1}`}
              className="max-w-full max-h-[60vh] mx-auto rounded-lg shadow-md border border-neutral-700/30 dark:border-neutral-700/30 bg-white dark:bg-[#1a1a1a]"
            />
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default TarotMap;